import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faCalendarAlt, faMapMarkerAlt, faUsers, faClock } from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';

function EditProgram() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'workshop',
    program_date: '',
    program_time: '',
    end_date: '',
    end_time: '',
    location: '',
    location_details: '',
    capacity: '',
    member_price: '',
    non_member_price: '',
    status: 'upcoming',
    featured: false
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchProgram();
  }, [id]);
  
  const fetchProgram = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getProgram(id);
      
      // Split datetime into date and time components for form inputs
      const formatDateTimeForInput = (isoString) => {
        if (!isoString) return { date: '', time: '' };
        const dateObj = new Date(isoString);
        
        // Format date as YYYY-MM-DD
        const date = dateObj.toISOString().split('T')[0];
        
        // Format time as HH:MM
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const time = `${hours}:${minutes}`;
        
        return { date, time };
      };
      
      const programDateTime = formatDateTimeForInput(data.program_date);
      const endDateTime = formatDateTimeForInput(data.end_date);
      
      setFormData({
        ...data,
        program_date: programDateTime.date,
        program_time: programDateTime.time,
        end_date: endDateTime.date || '',
        end_time: endDateTime.time || '',
        capacity: data.capacity?.toString() || '',
        member_price: data.member_price?.toString() || '',
        non_member_price: data.non_member_price?.toString() || ''
      });
      
    } catch (error) {
      console.error('Error fetching program:', error);
      setError('Failed to load program data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
    
    // If program_date changes, check if we need to reset end_date
    if (name === 'program_date' && formData.end_date) {
      const newStartDate = new Date(value);
      const currentEndDate = new Date(formData.end_date);
      
      if (currentEndDate < newStartDate) {
        // Reset end_date if it's now invalid
        setFormData(prev => ({
          ...prev,
          end_date: value // Set end_date to same as new start date
        }));
      }
    }
    
    // Additional validation for end_time when program_date and end_date are the same
    if ((name === 'program_time' || name === 'end_time' || name === 'program_date' || name === 'end_date')) {
      // Get the updated values
      const updatedValues = {
        ...formData,
        [name]: value
      };
      
      const programDate = name === 'program_date' ? value : updatedValues.program_date;
      const endDate = name === 'end_date' ? value : updatedValues.end_date;
      const programTime = name === 'program_time' ? value : updatedValues.program_time;
      const endTime = name === 'end_time' ? value : updatedValues.end_time;
      
      if (programDate && endDate && programDate === endDate && programTime && endTime) {
        if (endTime <= programTime) {
          setErrors(prev => ({
            ...prev,
            end_time: 'End time must be later than start time'
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            end_time: null
          }));
        }
      }
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title) newErrors.title = 'Program title is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.program_date) newErrors.program_date = 'Start date is required';
    if (!formData.location) newErrors.location = 'Location is required';
    
    // Enhanced Date and Time validations
    if (formData.end_date && formData.program_date) {
      const startDate = new Date(formData.program_date);
      const endDate = new Date(formData.end_date);
      
      if (endDate < startDate) {
        newErrors.end_date = 'End date cannot be before start date';
      } else if (endDate.getTime() === startDate.getTime()) {
        // If same day, validate end time is after start time
        if (formData.program_time && formData.end_time) {
          if (formData.end_time <= formData.program_time) {
            newErrors.end_time = 'End time must be later than start time';
          }
        }
      }
    }
    
    // Validate program time is provided when program date is provided
    if (formData.program_date && !formData.program_time) {
      newErrors.program_time = 'Start time is required when start date is provided';
    }
    
    // Validate end time is provided when end date is provided
    if (formData.end_date && !formData.end_time) {
      newErrors.end_time = 'End time is required when end date is provided';
    }
    
    if (formData.capacity && isNaN(formData.capacity)) {
      newErrors.capacity = 'Capacity must be a number';
    }
    
    if (formData.member_price && isNaN(formData.member_price)) {
      newErrors.member_price = 'Member price must be a number';
    }
    
    if (formData.non_member_price && isNaN(formData.non_member_price)) {
      newErrors.non_member_price = 'Non-member price must be a number';
    }
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Format date and time for API submission
      const formatDateTimeForAPI = (dateString, timeString) => {
        if (!dateString) return null;
        // Combine date and time strings into ISO format
        return `${dateString}T${timeString}:00`;
      };
      
      // Format data for API submission
      const programData = {
        ...formData,
        program_date: formatDateTimeForAPI(formData.program_date, formData.program_time),
        end_date: formData.end_date ? formatDateTimeForAPI(formData.end_date, formData.end_time) : null,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        member_price: formData.member_price ? parseFloat(formData.member_price) : 0,
        non_member_price: formData.non_member_price ? parseFloat(formData.non_member_price) : 0
      };
      
      // Submit to API
      await adminService.updateProgram(id, programData);
      
      // Redirect to program details page after successful submission
      navigate(`/admin/programs`);
    } catch (error) {
      console.error('Error updating program:', error);
      setError('Failed to update program. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading program data...</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="admin-page-header">
          <h1 className="admin-page-title">Edit Program</h1>
          <ul className="admin-breadcrumb">
            <li className="admin-breadcrumb-item">
              <Link to="/admin/dashboard">Dashboard</Link>
            </li>
            <li className="admin-breadcrumb-item">
              <Link to="/admin/programs">Programs</Link>
            </li>
            <li className="admin-breadcrumb-item active">Edit Program</li>
          </ul>
        </div>
        
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '1rem', 
          borderRadius: '0.25rem',
          marginBottom: '1rem' 
        }}>
          {error}
          <div style={{ marginTop: '1rem' }}>
            <button 
              className="admin-btn admin-btn-primary" 
              onClick={() => navigate('/admin/programs')}
            >
              Back to Programs
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Edit Program</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/programs">Programs</Link>
          </li>
          <li className="admin-breadcrumb-item active">Edit "{formData.title}"</li>
        </ul>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Program Information</h2>
        </div>
        <div className="admin-card-body">
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="admin-form-group">
              <label htmlFor="title" className="admin-form-label">Program Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`admin-form-control ${errors.title ? 'admin-input-error' : ''}`}
              />
              {errors.title && <div className="admin-error-message">{errors.title}</div>}
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="type" className="admin-form-label">Program Type *</label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="admin-form-control"
                >
                  <option value="workshop">Workshop</option>
                  <option value="webinar">Webinar</option>
                  <option value="course">Course</option>
                  <option value="conference">Conference</option>
                  <option value="seminar">Seminar</option>
                </select>
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="status" className="admin-form-label">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="admin-form-control"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="description" className="admin-form-label">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={`admin-form-control ${errors.description ? 'admin-input-error' : ''}`}
                rows="4"
              ></textarea>
              {errors.description && <div className="admin-error-message">{errors.description}</div>}
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="program_date" className="admin-form-label">Start Date *</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="date"
                    id="program_date"
                    name="program_date"
                    value={formData.program_date}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.program_date ? 'admin-input-error' : ''}`}
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.program_date && <div className="admin-error-message">{errors.program_date}</div>}
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="program_time" className="admin-form-label">Start Time *</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faClock} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="time"
                    id="program_time"
                    name="program_time"
                    value={formData.program_time}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.program_time ? 'admin-input-error' : ''}`}
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.program_time && <div className="admin-error-message">{errors.program_time}</div>}
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="end_date" className="admin-form-label">End Date</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    min={formData.program_date} // This prevents selecting dates before the start date
                    className={`admin-form-control ${errors.end_date ? 'admin-input-error' : ''}`}
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.end_date && <div className="admin-error-message">{errors.end_date}</div>}
                <small className="admin-form-text">Leave empty for single-day events</small>
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="end_time" className="admin-form-label">End Time</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faClock} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="time"
                    id="end_time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    className="admin-form-control"
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 2 }}>
                <label htmlFor="location" className="admin-form-label">Location *</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.location ? 'admin-input-error' : ''}`}
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.location && <div className="admin-error-message">{errors.location}</div>}
              </div>
              
              <div className="admin-form-group">
                <label htmlFor="capacity" className="admin-form-label">Capacity</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faUsers} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="number"
                    id="capacity"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.capacity ? 'admin-input-error' : ''}`}
                    min="0"
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.capacity && <div className="admin-error-message">{errors.capacity}</div>}
                <small className="admin-form-text">Leave empty for unlimited capacity</small>
              </div>
            </div>
            
            <div className="admin-form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="location_details" className="admin-form-label">Location Details</label>
              <textarea
                id="location_details"
                name="location_details"
                value={formData.location_details}
                onChange={handleChange}
                className="admin-form-control"
                rows="2"
              ></textarea>
            </div>

            <div className="admin-form-row" style={{ marginBottom: '20px' }}>
              <div className="admin-form-group" style={{ flex: 1, marginRight: '15px' }}>
                <label htmlFor="member_price" className="admin-form-label">Member Registration Fee (₹)</label>
                <input
                  type="text"
                  id="member_price"
                  name="member_price"
                  value={formData.member_price}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.member_price ? 'admin-input-error' : ''}`}
                />
                {errors.member_price && <div className="admin-error-message">{errors.member_price}</div>}
                <small className="admin-form-text">Leave empty for free registration for members</small>
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="non_member_price" className="admin-form-label">Non-Member Registration Fee (₹)</label>
                <input
                  type="text"
                  id="non_member_price"
                  name="non_member_price"
                  value={formData.non_member_price}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.non_member_price ? 'admin-input-error' : ''}`}
                />
                {errors.non_member_price && <div className="admin-error-message">{errors.non_member_price}</div>}
                <small className="admin-form-text">Leave empty for free registration for non-members</small>
              </div>
            </div>
            
            <div className="admin-form-group" style={{ marginTop: '10px', marginBottom: '30px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="featured"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem' }}
                />
                <label htmlFor="featured">
                  <strong>Feature this program</strong><br />
                  <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>Featured programs appear prominently on the website</span>
                </label>
              </div>
            </div>
            
            <div className="admin-form-actions" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Link
                to="/admin/programs"
                className="admin-btn admin-btn-light admin-btn-with-icon"
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>Cancel</span>
              </Link>
              <button
                type="submit"
                className="admin-btn admin-btn-primary admin-btn-with-icon"
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={faSave} />
                <span>{isSubmitting ? 'Saving...' : 'Update Program'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}

export default EditProgram;
